<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import GlobalSearchPopover from '@/components/global-search/GlobalSearchPopover.vue'
import { AnalyticalEvents, trackEvent } from '@/utils/analytics'
import {
    getPreferredGlobalSearchPlatformId,
    isSupportedPlatformId,
    useGlobalCreatorSearch,
} from './global-search/useGlobalCreatorSearch'

const store = useStore()
const router = useRouter()
const route = useRoute()
const instance = getCurrentInstance()
const open = ref(false)
const storedPlatformId = computed(() => store.getters.preferences?.globalSearch?.platformId)
const defaultPlatformId = computed(() => store.getters.selfDefaultPlatformId)
const initialPlatformId = computed(() =>
    getPreferredGlobalSearchPlatformId(storedPlatformId.value, defaultPlatformId.value),
)

const {
    query,
    selectedPlatform,
    visibleResults,
    pendingResults,
    trimmedQuery,
    isSearchableQuery,
    isInitialLoading,
    isLookingForCreators,
    canSearchMoreCreators,
    noCreatorsFound,
    addRecentSearch,
    removeRecentSearch,
    clearRecentSearches,
    setQueryAndSearchImmediately,
    setSelectedPlatform,
    applyPreferredPlatform,
    searchMoreCreators,
    showMoreResults,
    clearSearch,
} = useGlobalCreatorSearch({
    store,
    onError: (error, options) => instance?.proxy?.$errorHandler(error, options),
    initialPlatform: initialPlatformId.value,
})

const recentSearches = computed(() => store.getters.recentSearches || [])

const selectSearchTerm = async (searchTerm) => {
    await setQueryAndSearchImmediately(searchTerm)
}

const selectCreator = (creator) => {
    addRecentSearch(trimmedQuery.value)

    trackEvent(AnalyticalEvents.ProfileUnlocked, {
        creator_count: 1,
        origin: 'Global search bar',
    })

    router.push({
        path: route.path,
        query: {
            ...route.query,
            modal: 'creator',
            platform_id: creator.platformId,
            platform_user_id: creator.id,
        },
    })

    open.value = false
}

watch([storedPlatformId, defaultPlatformId], ([storedPlatform, defaultPlatform]) => {
    if (isSupportedPlatformId(storedPlatform)) {
        applyPreferredPlatform(storedPlatform)
        return
    }

    if (isSupportedPlatformId(defaultPlatform)) {
        applyPreferredPlatform(defaultPlatform)
    }
})
</script>

<template>
    <GlobalSearchPopover
        v-model:open="open"
        v-model:query="query"
        :platform="selectedPlatform"
        :recent-searches="recentSearches"
        :visible-results="visibleResults"
        :pending-results="pendingResults"
        :is-searchable-query="isSearchableQuery"
        :is-initial-loading="isInitialLoading"
        :is-looking-for-creators="isLookingForCreators"
        :can-search-more-creators="canSearchMoreCreators"
        :no-creators-found="noCreatorsFound"
        @clear-search="clearSearch"
        @select-search-term="selectSearchTerm"
        @remove-recent-search="removeRecentSearch"
        @clear-recent-searches="clearRecentSearches"
        @update:platform="setSelectedPlatform"
        @search-more-creators="searchMoreCreators"
        @show-more-results="showMoreResults"
        @select-creator="selectCreator"
    />
</template>
